import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Share2, Copy, Download, Twitter, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";
import useSeo from "../hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImpactShare() {
  const { t, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const scope = params.get("scope") || "all";
  const [stats, setStats] = useState(null);
  const [presets, setPresets] = useState([{ key: "all", label: "All deployments", kind: "all" }]);

  const ogUrl = useMemo(() => `${API}/impact/og.png?scope=${scope}`, [scope]);
  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const u = new URL(window.location.href);
    u.searchParams.set("scope", scope);
    return u.toString();
  }, [scope]);

  useSeo({
    title: `Fresh Earth · Live Impact · ${scope === "all" ? "All deployments" : scope}`,
    description: "Cumulative outcomes across active OED deployments.",
    path: `/impact/share`,
    image: ogUrl,
  });

  useEffect(() => {
    axios.get(`${API}/impact/scopes`).then((r) => setPresets(r.data.presets || [])).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/impact/stats`, { params: { scope } }).then((r) => setStats(r.data)).catch(() => {});
  }, [scope]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("Link copied", "लिंक कॉपी हो गया"));
    } catch {
      toast.error(t("Could not copy", "कॉपी नहीं हो सका"));
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Fresh Earth · Live Impact",
          text: stats
            ? `${stats.tonnes_treated.toLocaleString("en-IN")} tonnes treated with OED — live from active sites.`
            : "Fresh Earth · Live Impact",
          url: shareUrl,
        });
        return;
      } catch { /* user cancelled */ }
    }
    copyLink();
  };

  const twitterHref = useMemo(() => {
    const text = stats
      ? `${stats.tonnes_treated.toLocaleString("en-IN")} tonnes of waste treated with Fresh Earth OED — live from active sites.`
      : "Fresh Earth · Live Impact";
    return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
  }, [stats, shareUrl]);

  const linkedInHref = useMemo(
    () => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    [shareUrl]
  );

  return (
    <div className="min-h-screen bg-bone text-ink" data-testid="impact-share-page">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-14">
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <Link to="/" data-testid="share-back" className="inline-flex items-center gap-2 text-slate hover:text-ink text-xs tracking-[0.2em] uppercase">
            <ArrowLeft size={14} /> {t("Back to site", "साइट पर वापस")}
          </Link>
          <div className="text-xs tracking-[0.22em] uppercase text-terracotta font-semibold">
            {t("Public Impact Feed", "सार्वजनिक प्रभाव फ़ीड")}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Poster preview */}
          <div className="lg:col-span-8">
            <div className="rounded-3xl overflow-hidden border border-stone/20 shadow-sm bg-sand" data-testid="share-poster">
              <img
                src={ogUrl}
                alt="Fresh Earth Live Impact poster"
                className="w-full h-auto block"
                data-testid="share-og-image"
              />
            </div>
            <p className="mt-4 text-sm text-stone" data-testid="share-poster-caption">
              {t(
                "1200 × 630px poster — auto-generated with today's numbers. Perfect for LinkedIn, X, and press kits.",
                "1200 × 630px पोस्टर — आज के आँकड़ों के साथ स्वतः निर्मित। LinkedIn, X और प्रेस किट के लिए उपयुक्त।"
              )}
            </p>
          </div>

          {/* Right rail */}
          <div className="lg:col-span-4 space-y-8">
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-moss font-semibold">
                {t("Filter", "फ़िल्टर")}
              </div>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="share-scope-chips">
                {presets.map((p) => {
                  const active = p.key === scope;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      data-testid={`share-scope-${p.key}`}
                      onClick={() => {
                        const next = new URLSearchParams(params);
                        next.set("scope", p.key);
                        setParams(next);
                      }}
                      className={`rounded-full px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase border transition-colors ${
                        active
                          ? "bg-forest text-bone border-forest"
                          : "border-stone/30 text-slate hover:text-ink"
                      }`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-3">
              <button
                type="button"
                onClick={nativeShare}
                data-testid="share-native"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-forest text-bone px-6 py-3 text-sm tracking-[0.1em] uppercase hover:bg-moss transition-colors"
              >
                <Share2 size={15} /> {t("Share", "साझा करें")}
              </button>
              <button
                type="button"
                onClick={copyLink}
                data-testid="share-copy-link"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-stone/30 px-6 py-3 text-sm hover:bg-sand transition-colors"
              >
                <Copy size={15} /> {t("Copy link", "लिंक कॉपी करें")}
              </button>
              <a
                href={ogUrl}
                download={`fresh-earth-impact-${scope}.png`}
                data-testid="share-download-png"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-stone/30 px-6 py-3 text-sm hover:bg-sand transition-colors"
              >
                <Download size={15} /> {t("Download PNG", "PNG डाउनलोड करें")}
              </a>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <a
                  href={twitterHref}
                  target="_blank" rel="noreferrer"
                  data-testid="share-twitter"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-stone/30 px-4 py-2.5 text-xs hover:bg-sand transition-colors"
                >
                  <Twitter size={13} /> Post to X
                </a>
                <a
                  href={linkedInHref}
                  target="_blank" rel="noreferrer"
                  data-testid="share-linkedin"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-stone/30 px-4 py-2.5 text-xs hover:bg-sand transition-colors"
                >
                  <Linkedin size={13} /> LinkedIn
                </a>
              </div>
            </div>

            <div className="rounded-2xl bg-sand border border-stone/20 p-6">
              <div className="text-[10px] tracking-[0.22em] uppercase text-stone font-semibold">
                {t("Snapshot", "स्नैपशॉट")}
              </div>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate">{t("Tonnes treated", "उपचारित टन")}</span>
                  <span className="font-serif text-ink tabular-nums">{stats ? stats.tonnes_treated.toLocaleString("en-IN") : "…"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate">{t("tCO₂e avoided", "tCO₂e से बचाव")}</span>
                  <span className="font-serif text-ink tabular-nums">{stats ? stats.co2e_avoided_tonnes.toLocaleString("en-IN") : "…"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate">{t("Odour-free site-days", "गंध-मुक्त साइट-दिवस")}</span>
                  <span className="font-serif text-ink tabular-nums">{stats ? stats.odour_free_days.toLocaleString("en-IN") : "…"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate">{t("Active deployments", "सक्रिय तैनातियाँ")}</span>
                  <span className="font-serif text-ink tabular-nums">{stats ? stats.sites_active : "…"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
